import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import TranslateIcon from '@mui/icons-material/Translate';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import BuildIcon from '@mui/icons-material/Build';
import ArticleIcon from '@mui/icons-material/Article';
import InfoIcon from '@mui/icons-material/Info';

interface MobileReportEditorProps {
  chatMessage: string;
  setChatMessage: (message: string) => void;
  isChatProcessing: boolean;
  handleChatSubmit: () => void;
  saveChanges: () => void;
  openTranslateMenu: (event: React.MouseEvent<HTMLElement>) => void;
  handleAutoMarkdown: () => void;
  handleUndo: () => void;
  handleRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  handlePatientReportPrompt: () => void;
  handleAdvisoryNotePrompt: () => void;
}

const MobileReportEditor: React.FC<MobileReportEditorProps> = ({
  chatMessage,
  setChatMessage,
  isChatProcessing,
  handleChatSubmit,
  saveChanges,
  openTranslateMenu,
  handleAutoMarkdown,
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,
  handlePatientReportPrompt,
  handleAdvisoryNotePrompt,
}) => {
  const theme = useTheme();

  const handleSaveAndExit = () => {
    saveChanges();
    // Exit edit mode handled by parent
  };

  return (
    <>
      {/* Mobile AI Chat Footer Bar - Fixed at bottom */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          flexDirection: 'column',
          background: theme.palette.background.paper,
          borderTop: `2px solid ${theme.palette.divider}`,
          boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* AI Thinking Indicator */}
        {isChatProcessing && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              background: theme.palette.mode === 'dark'
                ? 'rgba(0, 122, 255, 0.1)'
                : 'rgba(0, 122, 255, 0.05)',
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <img
                src="/assets/noetiv.png"
                alt="Noetiv AI"
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Noetiv AI is thinking
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
                {[0, 0.15, 0.3].map((delay, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
                      animation: 'bounce 1.4s infinite ease-in-out both',
                      animationDelay: `${delay}s`,
                      '@keyframes bounce': {
                        '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.5 },
                        '40%': { transform: 'scale(1)', opacity: 1 },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Chat Input Bar */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            placeholder="Ask Noetiv to refine your report..."
            variant="outlined"
            size="small"
            disabled={isChatProcessing}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleChatSubmit();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 20,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleChatSubmit}
            disabled={!chatMessage.trim() || isChatProcessing}
            sx={{
              width: 44,
              height: 44,
              background: chatMessage.trim() && !isChatProcessing
                ? 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)'
                : theme.palette.action.disabledBackground,
              color: '#fff',
              '&:hover': {
                background: chatMessage.trim() && !isChatProcessing
                  ? 'linear-gradient(135deg, #0051D5 0%, #003da5 100%)'
                  : theme.palette.action.disabledBackground,
                transform: chatMessage.trim() && !isChatProcessing ? 'scale(1.05)' : 'none',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <SendIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile FAB for AI Tools */}
      <SpeedDial
        ariaLabel="AI Tools"
        sx={{
          position: 'fixed',
          bottom: 140,
          right: 24,
          display: { xs: 'flex', md: 'none' },
          '& .MuiFab-primary': {
            background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
            boxShadow: '0 8px 24px rgba(0,122,255,0.4)',
          },
        }}
        icon={<SpeedDialIcon icon={<BuildIcon />} />}
      >
        <SpeedDialAction
          icon={<SaveIcon />}
          tooltipTitle="Save Changes"
          onClick={handleSaveAndExit}
          FabProps={{
            sx: {
              background: 'linear-gradient(135deg, #34C759 0%, #248A3D 100%)',
            },
          }}
        />
        <SpeedDialAction
          icon={<ArticleIcon />}
          tooltipTitle="Patient Report"
          onClick={handlePatientReportPrompt}
          FabProps={{
            sx: {
              background: 'linear-gradient(135deg, #007AFF 0%, #0051D5 100%)',
            },
          }}
        />
        <SpeedDialAction
          icon={<InfoIcon />}
          tooltipTitle="Advisory Note"
          onClick={handleAdvisoryNotePrompt}
          FabProps={{
            sx: {
              background: 'linear-gradient(135deg, #FF3B30 0%, #CC2E26 100%)',
            },
          }}
        />
        <SpeedDialAction
          icon={<TranslateIcon />}
          tooltipTitle="Translate"
          onClick={openTranslateMenu}
          FabProps={{
            sx: {
              background: 'linear-gradient(135deg, #AF52DE 0%, #7C3AA3 100%)',
            },
          }}
        />
        <SpeedDialAction
          icon={<AutoFixHighIcon />}
          tooltipTitle="Auto-Format"
          onClick={handleAutoMarkdown}
          FabProps={{
            sx: {
              background: 'linear-gradient(135deg, #FF9500 0%, #CC7700 100%)',
            },
          }}
        />
        <SpeedDialAction
          icon={<UndoIcon />}
          tooltipTitle="Undo"
          onClick={handleUndo}
          FabProps={{
            disabled: !canUndo,
          }}
        />
        <SpeedDialAction
          icon={<RedoIcon />}
          tooltipTitle="Redo"
          onClick={handleRedo}
          FabProps={{
            disabled: !canRedo,
          }}
        />
      </SpeedDial>
    </>
  );
};

export default MobileReportEditor;
